import { CoursePageFooter } from "@/components/course/CoursePageFooter";
import { CoursePagePremiumLoader } from "@/components/course/CoursePagePremiumLoader";
import { HelpScoutBeacon } from "@/components/HelpScoutBeacon";
import { BannerHero } from "@/components/sections/BannerHero";
import { ErrorDisplay, StatusBadge } from "@/components/ui";
import type { CourseSkillLevel } from "@/lib/constants";
import { useAutoHideScrollbar } from "@/lib/useDeviceType";
import React from "react";
import { Platform, RefreshControl } from "react-native";
import { ScrollView, YStack } from "tamagui";

type CoursePageShellProps = {
  courseId?: string;
  courseTitle?: string | null;
  courseSummary?: string | null;
  courseAvatarUrl?: string | null;
  courseSkillLevel?: CourseSkillLevel | null;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  refreshing: boolean;
  onRefresh: () => void;
  children?: React.ReactNode;
  showBanner?: boolean;
  /** Course status (0/1/2/3); used to render a DRAFT/RETIRED overlay badge. */
  courseStatus?: number | null;
  /** Whether the current user may see non-published content (gates the badge). */
  canViewDrafts?: boolean;
  /**
   * Optional custom scroller. When provided, the shell renders this instead of
   * its built-in ScrollView, passing the banner and footer so the caller can
   * place them inside a virtualized list (e.g. FlatList ListHeader/Footer).
   */
  renderScroller?: (parts: {
    banner: React.ReactElement | null;
    footer: React.ReactElement;
  }) => React.ReactElement;
};

export function CoursePageShell({
  courseId,
  courseTitle,
  courseSummary,
  courseAvatarUrl,
  courseSkillLevel,
  isLoading,
  error,
  onRetry,
  refreshing,
  onRefresh,
  children,
  showBanner = true,
  courseStatus,
  canViewDrafts = false,
  renderScroller,
}: CoursePageShellProps) {
  const { scrollbarStyle, onScroll } = useAutoHideScrollbar();

  if (isLoading) {
    return (
      <YStack f={1} bg="$bg">
        <HelpScoutBeacon />
        <ScrollView
          style={{ flex: 1, ...scrollbarStyle }}
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: "center",
            paddingBottom: Platform.OS === "web" ? 0 : 16,
          }}
          showsVerticalScrollIndicator={Platform.OS === "web"}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          <CoursePagePremiumLoader variant="course" />
        </ScrollView>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack f={1} bg="#F8F9FA">
        <ErrorDisplay error={error} onRetry={onRetry} />
      </YStack>
    );
  }

  const banner = showBanner ? (
    <BannerHero
      courseId={courseId}
      courseTitle={courseTitle ?? undefined}
      courseSummary={courseSummary ?? undefined}
      courseAvatarUrl={courseAvatarUrl}
      courseSkillLevel={courseSkillLevel ?? null}
    />
  ) : null;

  if (renderScroller) {
    return (
      <YStack f={1} bg="$bg">
        <HelpScoutBeacon />
        <StatusBadge status={courseStatus} visible={canViewDrafts} overlay />
        {renderScroller({ banner, footer: <CoursePageFooter /> })}
      </YStack>
    );
  }

  return (
    <YStack f={1} bg="$bg">
      <HelpScoutBeacon />
      <StatusBadge status={courseStatus} visible={canViewDrafts} overlay />
      <ScrollView
        style={{ flex: 1, ...scrollbarStyle }}
        contentContainerStyle={{
          flexGrow: 1,
          ...(Platform.OS === "web" ? { minHeight: "100%" } : {}),
          paddingBottom: Platform.OS === "web" ? 0 : 16,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
        onScroll={onScroll}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {banner}
        {children}
        {Platform.OS === "web" ? (
          <YStack flexGrow={1} minHeight={1} width="100%" />
        ) : null}
        <CoursePageFooter />
      </ScrollView>
    </YStack>
  );
}
